/**
 * THE THREE STATES OF A SERVER-BACKED READ, RENDERED ONCE. **Phase 2 §5.**
 *
 * `useServerData` keeps `loading`, `loaded` and `failed` apart; this is the component that keeps
 * them apart ON SCREEN. Without it every converted screen would write the same three-branch ternary,
 * and — going by what happened to writes before `<ActionFailureNotice>` existed — the twelfth screen
 * would shorten it to `rows ?? []` and silently tell an office it has no users when its request was
 * refused. The erasure would then have to be done once, here, in a file whose subject is not doing it.
 *
 * ── THE CHILDREN ARE A FUNCTION, AND THAT IS THE POINT ──────────────────────────────────────────
 *
 * `children` receives the DATA, so it cannot run until the data exists. A screen therefore cannot
 * render its table against a value that is secretly "empty because the read failed" — that state has
 * no `data` to hand it. The empty-but-successful case stays where it belongs: inside the table, in
 * `emptyStateHe`, which is a sentence about the office's own records rather than about the request.
 *
 * ── LOADING IS VISIBLE ──────────────────────────────────────────────────────────────────────────
 *
 * §5 is explicit that loading must not be hidden to keep older assertions passing. `SkeletonRows`
 * occupies the space the rows will occupy, so the screen does not jump when they arrive, and the
 * caption says what is being fetched rather than a bare spinner.
 *
 * NOTHING HERE DECIDES PERMISSION. It renders a decision that arrived, or the fact that none did.
 */

import type { ReactNode } from 'react';

import type { ActionFailure } from '../api/actionOutcome';
import type { ServerData } from '../api/useServerData';
import { ActionFailureNotice } from './ActionFailureNotice';
import { SkeletonRows } from './StateBlock';

export interface ServerReadProps<T> {
  readonly result: ServerData<T>;
  /** What is being read, in Hebrew, for the loading caption: `'משתמשי המשרד'`. */
  readonly whatHe: string;
  /** Rendered ONLY when the data exists. */
  readonly children: (data: T) => ReactNode;
  /** A conflict body this screen knows how to read. Same contract as `<ActionFailureNotice>`. */
  readonly renderConflict?: (failure: Extract<ActionFailure, { kind: 'conflict' }>) => ReactNode;
  /**
   * What a 404 MEANS on this route, in the screen's own words.
   *
   * Same argument as `renderConflict`, and the same boundary: only the screen knows which route it
   * called, and a 404 is route-specific in a way the generic wording cannot be. On the subscription
   * route it is `no_subscription` — a real state of a real office, with its own next step — and
   * rendering the generic "that was not found" there would tell a customer their billing screen is
   * broken when their subscription is merely absent.
   *
   * Omitted, the generic stands, which is honest rather than silent.
   */
  readonly renderNotFound?: () => ReactNode;
  /** How many placeholder rows to reserve while loading. Match the table's usual size. */
  readonly skeletonRows?: number;
}

export function ServerRead<T>({
  result,
  whatHe,
  children,
  renderConflict,
  renderNotFound,
  skeletonRows,
}: ServerReadProps<T>): JSX.Element {
  if (result.state === 'loading') {
    return (
      <section className="server-read" data-state="loading" aria-busy="true">
        {/* `aria-live` so a screen reader is told the wait ended, not left on a silent page. */}
        <p className="server-read__caption" aria-live="polite">{`טוען ${whatHe}…`}</p>
        <SkeletonRows {...(skeletonRows === undefined ? {} : { rows: skeletonRows })} />
      </section>
    );
  }

  if (result.state === 'failed') {
    const named =
      result.failure.kind === 'notFound' && renderNotFound !== undefined ? renderNotFound() : null;
    return (
      <section className="server-read" data-state="failed">
        {named}
        {/*
          The read did not arrive, and the reason is the server's own — a refusal renders as the
          contextless denial, an expired session as the step-up that names where to go, an outage as
          the outage. What is NOT rendered is an empty table, which is the sentence this component
          exists to prevent the product from saying.
        */}
        {named !== null ? null : (
          <ActionFailureNotice
            failure={result.failure}
            {...(renderConflict === undefined ? {} : { renderConflict })}
          />
        )}
      </section>
    );
  }

  return <>{children(result.data)}</>;
}
